import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DisplayMode, ExerciseComponent } from './ExerciseComponent';
import { SelectExercisesDrawer } from '@/screens/exercises/SelectExerciseDrawer';
import { filter, FilterProps } from '@/utils/exercise-filter';
import { AppContext } from '@/contexts/app.context';
import SearchIcon from '@mui/icons-material/Search';
import { Tooltip, Box, Button, InputAdornment, MenuItem, Paper, Select, Stack, TextField, Typography, IconButton, Grid } from '@mui/material';
import { Close, FilterList, ViewCompactOutlined, ViewList, ViewListOutlined } from '@mui/icons-material';
import { ViewCompact } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { CreateExerciseDTO, ExerciseDTO, DeckValidationDTO } from '@/generated';
import { UpdateDeckModal } from '@/screens/decks/UpdateDeckModal';
import { isDeckEditor } from '@/screens/decks/deck-utils';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { DefaultContainer } from '@/components/DefaultContainer';
import AddIcon from '@mui/icons-material/Add';
import { EditFab } from '@/components/EditFab';
import { useDeckQuery, useDeckExercisesQuery, useValidateDeckQuery, useAllExercisesQuery, useBodyZoneListQuery } from '@/hooks/query-hooks';
import { UpdateDeckExerciseModal } from './modals/UpdateDeckExerciseModal';
import { objectHasId } from '@/utils/create-mode-helper';
import { pathMap } from '@/navigation/paths';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import ExerciseCategoryAutocomplete from '../exercise-categories/ExerciseCategoryAutocomplete';
import { useAddExerciseToDeck } from '@/hooks/deck-mutation.hooks';
import EquipmentAutocomplete from '../equipment/EquipmentAutocomplete';
import { DeckImage } from '../decks/DeckImage';

const getDisplayModeLocalStorage = () => localStorage.getItem('exercisesDisplayMode');
const setDisplayModeLocalStorage = (displayMode: DisplayMode) => localStorage.setItem('exercisesDisplayMode', String(displayMode));

const getFilterPropsLocalStorage = () => JSON.parse(localStorage.getItem('exerciseFilterProps') ?? JSON.stringify({ categoryIds: [], public: true }));
const setFilterPropsLocalStorage = (filterProps: FilterProps) => localStorage.setItem('exerciseFilterProps', JSON.stringify(filterProps));

export const Exercises = () => {
  const context = useContext(AppContext);
  let user = context.state?.userProfile;
  const [showExerciseAdder, setShowExerciseAdder] = useState(false);
  const [showSetEditModal, setShowSetEditModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDTO | CreateExerciseDTO>();
  const [filterProps, setFilterProps] = useState<FilterProps>(getFilterPropsLocalStorage());
  const [filteredExercises, setFilteredExercises] = useState<Array<ExerciseDTO>>();
  const { deckId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const exerciseId = searchParams.get('exerciseId');
  const { deck } = useDeckQuery(deckId);
  const { validationResult, error: validationServerError, refetch: refetchValidation } = useValidateDeckQuery(deckId);
  const [displayMode, setDisplayMode] = useState(Number(getDisplayModeLocalStorage() ?? DisplayMode.List));
  const { exercises: deckExercises, error: loadDeckExercisesError } = useDeckExercisesQuery(deckId);
  const { exercises: allExercises, error: loadAllExercisesError } = useAllExercisesQuery(!deckId);
  const exercises = deckId === undefined ? allExercises : deckExercises;
  const addExerciseToDeckMutation = useAddExerciseToDeck();
  const { bodyZoneList } = useBodyZoneListQuery();

  const setAndStoreFilterProps = (filterProps: FilterProps) => {
    setFilterProps(filterProps);
    setFilterPropsLocalStorage(filterProps);
  };

  const setAndStoreDisplayMode = (displayMode: DisplayMode) => {
    setDisplayMode(displayMode);
    setDisplayModeLocalStorage(displayMode);
  };

  if (deck) context.setExtraTitle(' - ' + deck?.name);
  const navigate = useNavigate();

  useEffect(() => {
    if (exerciseId && exercises?.length) {
      setFilterProps({ name: exercises?.find((e) => e.id === exerciseId)?.name });
      navigate(location.pathname, { replace: true });
    }
  }, [exerciseId, exercises]);

  useEffect(() => {
    if (loadDeckExercisesError) enqueueSnackbar(`Failed to load Exercises ${loadDeckExercisesError}`, { variant: 'error' });
    if (validationServerError) enqueueSnackbar(`Failed to validate Deck ${validationServerError}`, { variant: 'error' });
  }, [loadDeckExercisesError, validationServerError]);

  useEffect(() => {
    if (filterProps && exercises) setFilteredExercises(filter(exercises, filterProps));
    else setFilteredExercises(exercises);
  }, [filterProps, exercises]);

  const exerciseClicked = async (e: ExerciseDTO) => {
    try {
      if (deck) await addExerciseToDeckMutation.mutateAsync({ deckId: deck?.id, exerciseId: e.id });
      enqueueSnackbar(`${e.name} added to Deck!`, { variant: 'success' });
      refetchValidation();
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar(JSON.parse(e.message).message, { variant: 'error' });
      }
    }
  };

  return (
    <>
      <SelectExercisesDrawer show={showExerciseAdder} setShow={setShowExerciseAdder} exerciseSelected={exerciseClicked} />
      <DefaultContainer>
        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <Grid container gap={{ xs: 0.5, sm: 2 }} direction={'row'} sx={{ alignContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
            {deck && (
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <DeckImage width={60} deck={deck} />
              </Box>
            )}
            {!deck && (
              <Tooltip title={'Create new exercise'}>
                <IconButton onClick={() => navigate(pathMap.EditExercise)}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
            {deck !== undefined && (
              <Tooltip title={'Add Exercise to this Deck'}>
                <IconButton
                  disabled={!isDeckEditor(deck, user)}
                  onClick={() => {
                    setShowExerciseAdder(true);
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}

            {deck?.id ? <ZoneValidator validationResult={validationResult} /> : ''}

            <Stack sx={{ flex: 1, justifyContent: 'flex-end' }} direction={'row'}>
              <IconButton onClick={() => setAndStoreDisplayMode(DisplayMode.List)}>{displayMode === DisplayMode.List ? <ViewList /> : <ViewListOutlined />}</IconButton>
              <IconButton onClick={() => setAndStoreDisplayMode(DisplayMode.Card)}>{displayMode === DisplayMode.Card ? <ViewCompact /> : <ViewCompactOutlined />}</IconButton>
            </Stack>
          </Grid>
        </Paper>
        <Paper
          sx={{
            my: 1,
            zIndex: 1,
            padding: 1,
            position: { xs: 'static', sm: 'sticky' },
            top: { sm: 125 },
          }}
        >
          <Grid container gap={{ xs: 1, sm: 2 }} direction={'row'} sx={{ alignContent: 'center', alignItems: 'center' }}>
            <TextField
              label="Filter"
              size="small"
              sx={{ flex: { xs: 1, sm: 'initial' }, minWidth: 0, maxWidth: { xs: 'none', sm: 200 } }}
              value={filterProps?.name}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setAndStoreFilterProps({ ...filterProps, name: '' });
                      }}
                      position="end"
                    >
                      <Close />
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(e: any) => {
                setAndStoreFilterProps({ ...filterProps, name: e.target.value });
              }}
            />
            <Button
              variant={showMobileFilters ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setShowMobileFilters((show) => !show)}
              aria-expanded={showMobileFilters}
              aria-controls="mobile-exercise-filters"
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, flexShrink: 0 }}
            >
              Filters
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
              {filteredExercises?.length} exercise(s)
            </Typography>
            <Stack
              id="mobile-exercise-filters"
              gap={{ xs: 1.5, sm: 2 }}
              direction={{ xs: 'column', sm: 'row' }}
              sx={{
                display: { xs: showMobileFilters ? 'flex' : 'none', sm: 'flex' },
                alignItems: { xs: 'stretch', sm: 'center' },
                width: { xs: '100%', sm: 'auto' },
                '& > *': { width: { xs: '100%', sm: 'auto' } },
              }}
            >
              <Select
                size="small"
                value={filterProps?.bodyZoneId !== '' && filterProps?.bodyZoneId ? filterProps?.bodyZoneId : 'any'}
                onChange={(e) => {
                  if (e.target.value !== 'any') setAndStoreFilterProps({ ...filterProps, bodyZoneId: e.target.value as any });
                  else setAndStoreFilterProps({ ...filterProps, bodyZoneId: '' });
                }}
              >
                <MenuItem key={'any'} value={'any'}>
                  Any Zone
                </MenuItem>

                {filterBodyzones(bodyZoneList ?? [], deck)?.map((bz) => (
                  <MenuItem key={bz.id} value={bz.id}>
                    {bz.name}
                  </MenuItem>
                ))}
              </Select>
              <ExerciseCategoryAutocomplete
                selectedCategoryIds={filterProps?.categoryIds ?? []}
                setSelectedCategoryIds={(ids) => setAndStoreFilterProps({ ...filterProps, categoryIds: ids })}
              />
              <EquipmentAutocomplete
                selectedEquipment={filterProps.equipmentIds?.map((id) => ({ equipmentId: id, optional: false })) ?? []}
                setSelectedEquipment={(eq) => setAndStoreFilterProps({ ...filterProps, equipmentIds: eq.map((e) => e.equipmentId) })}
              />
              <Stack sx={{ gap: 1, cursor: 'pointer', mx: { sm: 1 } }} direction={'row'} alignItems="center">
                <Typography variant="body2" sx={{ display: { xs: 'block', sm: 'none' } }}>
                  Visibility
                </Typography>
                {filterProps?.public === true ? <PublicIcon onClick={() => setAndStoreFilterProps({ ...filterProps, public: false })} /> : undefined}
                {filterProps?.public === false ? <PublicOffIcon onClick={() => setAndStoreFilterProps({ ...filterProps, public: undefined })} /> : undefined}
                {filterProps?.public === undefined ? <PublicIcon style={{ opacity: 0.25 }} onClick={() => setAndStoreFilterProps({ ...filterProps, public: true })} /> : undefined}
              </Stack>
            </Stack>
            <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>{filteredExercises?.length} Exercise(s) match the filter criteria.</Grid>
          </Grid>
        </Paper>
        {deck && (
          <EditFab
            setShow={(show) => {
              setShowSetEditModal(show);
            }}
          />
        )}

        {deck ? <UpdateDeckModal deck={deck} setShowCreateDeckModal={setShowSetEditModal} show={showSetEditModal} /> : undefined}

        {deck !== undefined && selectedExercise && objectHasId(selectedExercise) && (
          <UpdateDeckExerciseModal
            exercise={selectedExercise}
            show={selectedExercise !== undefined}
            deck={deck}
            onClose={() => {
              setSelectedExercise(undefined);
            }}
          />
        )}

        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Grid spacing={1} sx={{ my: 1, width: '100%' }} container>
            {filteredExercises?.map((e, i) => (
              <ExerciseComponent
                display={displayMode}
                timeout={1000}
                key={e.id}
                onExerciseDeleted={() => {
                  if (deck) refetchValidation();
                }}
                onClick={() => {
                  if (deck !== undefined) setSelectedExercise(e);
                  else navigate(`${pathMap.ExerciseDetails}/${e.id}`);
                }}
                deck={deck}
                exercise={e}
              />
            ))}
          </Grid>
        </Box>
      </DefaultContainer>
    </>
  );
};

const ZoneValidator = (props: { validationResult?: DeckValidationDTO }) => {
  const { validationResult } = props;

  return (
    <Grid>
      <Box sx={{ border: 1, p: 1, borderRadius: 1, color: validationResult?.isValid ? 'primary.main' : 'error.main' }}>
        {validationResult?.isValid ? (
          <Tooltip title={'The deck contains at least one exercise from each designated zone.'}>
            <Typography>Complete</Typography>
          </Tooltip>
        ) : (
          <Tooltip title={validationResult?.validationErrors}>
            <Typography>Incomplete</Typography>
          </Tooltip>
        )}
      </Box>
    </Grid>
  );
};
